// Reaction options offered in the selection popover, plus helpers to read a
// reaction back out of a stored comment body. Shared by the create widget
// (FeedbackWidget) and the read-only reader layer (SubmittedFeedbackLayer) so
// the labels/glyphs have a single source of truth.

export type FeedbackReaction = "resonates" | "doesNotResonate"

export const FEEDBACK_REACTION_ORDER: FeedbackReaction[] = [
  "resonates",
  "doesNotResonate",
]

export const FEEDBACK_REACTION_LABELS: Record<FeedbackReaction, string> = {
  resonates: "This resonates",
  doesNotResonate: "Meh",
}

export const FEEDBACK_REACTION_GLYPHS: Record<FeedbackReaction, string> = {
  resonates: "🔥",
  doesNotResonate: ":/",
}

// Separator used when a comment body bundles a reaction label with a note,
// e.g. "This resonates — their note". Keep in sync with how bodies are built.
export const FEEDBACK_REACTION_SEPARATOR = " — "

// Splits a stored comment body back into its reaction (if any) and free-text
// note. Mirrors how the body is assembled at submit time. Note: this is a
// best-effort string parse — a freeform note that literally begins with a
// reaction label could be misread. A dedicated DB column would remove the
// ambiguity if that ever matters.
export const parseReactionFromBody = (
  body: string
): { reaction: FeedbackReaction | null; note: string } => {
  const trimmed = body.trim()

  for (const reaction of FEEDBACK_REACTION_ORDER) {
    const label = FEEDBACK_REACTION_LABELS[reaction]
    if (trimmed === label) {
      return { reaction, note: "" }
    }
    const prefix = `${label}${FEEDBACK_REACTION_SEPARATOR}`
    if (trimmed.startsWith(prefix)) {
      return { reaction, note: trimmed.slice(prefix.length) }
    }
  }

  return { reaction: null, note: trimmed }
}
